// The pole table: one row per measurement, edited in place, 400 rows a page.
// GPL-3.0-or-later.
import * as S from "@dipfield/stereo";
import { t, tn } from "../../i18n/index.js";
import { $, esc } from "../../lib/dom.js";
import { toast } from "../../lib/toast.js";
import { pushHistory, undo } from "../../state/store.js";
import { clearData } from "../../state/ops.js";
import { X, P, on, update, draw, onRender } from "./ctx.js";
import { renderTraverse } from "./traverse.js";

const MAX_ROWS = 400;
function renderTable() {
  const body = $("tbody"), p = P(), d = p.data;
  const n = d.planes.length, L = S.FORMAT_LABELS[d.format];
  $("th-a").textContent = L[1]; $("th-b").textContent = L[2];
  const multiTr = d.traverses.length > 1; $("th-t").hidden = !multiTr;
  $("count").textContent = n ? tn(n, "{n} row", "{n} rows") : t("No data yet");
  const r2 = (x: number) => +x.toFixed(2);
  const pages = Math.max(1, Math.ceil(n / MAX_ROWS)); X.tablePage = Math.min(X.tablePage, pages - 1);
  const off = X.tablePage * MAX_ROWS, first = d.traverses[0]?.id;
  const opts = (sel: string | undefined) => d.traverses.map((tv, k) =>
    `<option value="${esc(tv.id)}" ${(sel ?? first) === tv.id || (k === 0 && !d.traverses.some((x) => x.id === sel)) ? "selected" : ""}>${esc(tv.name)}</option>`).join("");
  body.innerHTML = d.planes.slice(off, off + MAX_ROWS).map((q, j) => { const i = off + j;
    const [a, b] = S.planeToFormat(q.dip, q.dipDir, d.format);
    return `<tr data-i="${i}"><td class="idx">${i + 1}</td>
     <td><input aria-label="${L[1]} ${t("row {n}", { n: i + 1 })}" data-k="a" inputmode="decimal" value="${r2(a)}"></td>
     <td><input aria-label="${L[2]} ${t("row {n}", { n: i + 1 })}" data-k="b" inputmode="decimal" value="${r2(b)}"></td>
     <td><input aria-label="${t("Count")} ${t("row {n}", { n: i + 1 })}" data-k="q" inputmode="decimal" value="${q.quantity}"></td>
     ${multiTr ? `<td><select data-k="t" aria-label="Traverse ${t("row {n}", { n: i + 1 })}">${opts(q.traverse)}</select></td>` : ""}
     <td><button class="icon" data-del="${i}" aria-label="${t("Delete row {n}", { n: i + 1 })}" title="${t("Delete row")}">×</button></td></tr>`;
  }).join("") || `<tr><td colspan="6" style="padding:.9rem .6rem;color:var(--ink-3);font-size:.84rem;text-align:center">${t("No rows yet — import a file, paste data, or press “Add row”")}</td></tr>`;
  $("more").hidden = n <= MAX_ROWS;
  $("more").innerHTML = `<button class="btn small" id="pg-prev" ${X.tablePage ? "" : "disabled"}>${t("Previous")}</button>
    <span>${t("rows {a}–{b} of {n}", { a: off + 1, b: Math.min(n, off + MAX_ROWS), n })}</span>
    <button class="btn small" id="pg-next" ${X.tablePage < pages - 1 ? "" : "disabled"}>${t("Next")}</button>`;
  $<HTMLSelectElement>("p-format").value = d.format;
}

/** Show the page holding the last row and select its first cell. */
export function focusLastRow() {
  X.tablePage = Math.floor((P().data.planes.length - 1) / MAX_ROWS); update();
  const row = $("tbody").querySelector<HTMLTableRowElement>(`tr[data-i="${P().data.planes.length - 1}"]`);
  if (row) { row.scrollIntoView({ block: "nearest" }); row.querySelector<HTMLInputElement>('[data-k="a"]')?.select(); }
}

export function mountTable() {
  onRender((table) => { if (table) renderTable(); });
  renderTable();
  on($("more"), "click", (e: Event) => {
    const id = (e.target as HTMLElement).id;
    if (id === "pg-prev") { X.tablePage--; renderTable(); } else if (id === "pg-next") { X.tablePage++; renderTable(); }
  });
  const body = $("tbody");
  on(body, "change", (e: Event) => {
    const el = e.target as HTMLInputElement; const tr = el.closest("tr"); if (!tr || !el.dataset.k) return;
    const i = +tr.dataset.i!, p = P();
    const val = (k: string) => Number((tr.querySelector(`[data-k="${k}"]`) as HTMLInputElement).value.replace(",", "."));
    if (el.dataset.k === "t") { pushHistory(); p.data.planes[i].traverse = (el as unknown as HTMLSelectElement).value; update(false); renderTraverse(); return; }
    if (el.dataset.k === "q") {
      const q = val("q"); const ok = Number.isFinite(q) && q > 0; el.classList.toggle("bad", !ok);
      if (ok) { pushHistory(); p.data.planes[i].quantity = q; update(false); } return;
    }
    const pl = S.formatToPlane(val("a"), val("b"), p.data.format);
    tr.querySelectorAll('[data-k="a"],[data-k="b"]').forEach((x) => x.classList.toggle("bad", !pl));
    if (!pl) return;
    pushHistory(); p.data.planes[i].dip = pl.dip; p.data.planes[i].dipDir = pl.dipDir; update(false);
  });
  on(body, "click", (e: Event) => {
    const b = (e.target as HTMLElement).closest("[data-del]") as HTMLElement | null; if (!b) return;
    pushHistory(); P().data.planes.splice(+b.dataset.del!, 1); update();
  });
  on(body, "mouseover", (e: Event) => { const tr = (e.target as HTMLElement).closest<HTMLElement>("tr[data-i]"); const i = tr ? +tr.dataset.i! : -1; if (i !== X.hoverRow) { X.hoverRow = i; draw(); } });
  on(body, "mouseleave", () => { if (X.hoverRow >= 0) { X.hoverRow = -1; draw(); } });
  on($("addrow"), "click", () => { pushHistory(); P().data.planes.push({ dip: 45, dipDir: 0, quantity: 1 }); focusLastRow(); });
  on($("clear"), "click", () => {
    const n = P().data.planes.length; if (!n) return;
    pushHistory(); clearData(P()); X.tablePage = 0; update();
    toast(tn(n, "Deleted {n} row and every set", "Deleted {n} rows and every set"), undo);
  });
  on($("p-format"), "change", (e: Event) => { P().data.format = (e.target as HTMLSelectElement).value as S.OrientationFormat; update(); });
}
